import { useEffect, useState } from 'react';

// BoldSign posts client-side events from this origin.
const BOLDSIGN_ORIGIN = 'https://app.boldsign.com';

type BoldSignAction =
  | 'onDocumentSigned'
  | 'onDocumentSigningFailed'
  | 'onDocumentDeclined'
  | 'onDocumentDecliningFailed'
  | 'onDocumentReassigned'
  | 'onDocumentReassigningFailed';

interface BoldSignMessage {
  action?: BoldSignAction;
  [key: string]: unknown;
}

type SignStatus = 'signing' | 'completed' | 'declined' | 'failed';

interface EmbeddedSignProps {
  /** The signLink returned by GET /document/getEmbeddedSignLink */
  signLink?: string;
  onCompleted?: () => void;
  onDeclined?: () => void;
  onFailed?: (action: BoldSignAction) => void;
  className?: string;
}

/**
 * Embeds the BoldSign signing page. When every page is signed, BoldSign fires
 * `onDocumentSigned`; we then remove the iframe (closing the sign page) and
 * show the completed state.
 */
export default function EmbeddedSign({ signLink, onCompleted, onDeclined, onFailed, className }: EmbeddedSignProps) {
  const [status, setStatus] = useState<SignStatus>('signing');

  useEffect(() => {
    function handleMessage(event: MessageEvent<BoldSignMessage>) {
      // Only trust messages from BoldSign.
      if (event.origin !== BOLDSIGN_ORIGIN) return;

      const action = event.data?.action;
      switch (action) {
        case 'onDocumentSigned':
          setStatus('completed');
          onCompleted?.();
          break;
        case 'onDocumentDeclined':
          setStatus('declined');
          onDeclined?.();
          break;
        case 'onDocumentSigningFailed':
        case 'onDocumentDecliningFailed':
          setStatus('failed');
          onFailed?.(action);
          break;
        default:
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onCompleted, onDeclined, onFailed]);

  if (status === 'completed') {
    return <div style={{ padding: 24, color: '#2f9e44', fontWeight: 600 }}>✓ Document completed. Signing closed.</div>;
  }
  if (status === 'declined') {
    return <div style={{ padding: 24, color: '#e03131' }}>You declined the document.</div>;
  }
  if (status === 'failed') {
    return <div style={{ padding: 24, color: '#e03131' }}>Signing failed. Please try again.</div>;
  }
  if (!signLink) {
    return <div style={{ padding: 24 }}>Preparing document…</div>;
  }

  return (
    <iframe
      title='BoldSign signing'
      src={signLink}
      className={className}
      style={{ width: '100%', height: '80vh', border: 0, borderRadius: 12 }}
    />
  );
}
